"use client";

import { useMemo, type RefObject } from "react";
import { useDismissPopoverOnScroll } from "./useDismissPopoverOnScroll";
import { format, parse } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { MarketplaceDateFilterValue } from "@/types/api/marketplace";
import { ChevronDown, X } from "lucide-react";
import {
  filterChipActiveClass,
  filterChipInactiveClass,
} from "./filterStyles";
import { MarketplaceDatePickerPanel } from "./MarketplaceDatePickerPanel";

interface DateRangeFilterProps {
  label: string;
  value?: MarketplaceDateFilterValue;
  onChange: (value: MarketplaceDateFilterValue | undefined) => void;
  isActive?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  className?: string;
}

function parseDate(iso?: string): Date | undefined {
  if (!iso) return undefined;
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export function DateRangeFilter({
  label,
  value,
  onChange,
  isActive,
  open,
  onOpenChange,
  scrollContainerRef,
  className,
}: DateRangeFilterProps) {
  useDismissPopoverOnScroll(open, () => onOpenChange(false), scrollContainerRef);

  const selected: DateRange | undefined = useMemo(
    () => ({
      from: parseDate(value?.start),
      to: parseDate(value?.end),
    }),
    [value?.start, value?.end]
  );

  const rangeLabel = useMemo(() => {
    if (selected?.from && selected?.to) {
      return `${format(selected.from, "MMM d")} – ${format(selected.to, "MMM d")}`;
    }
    if (selected?.from) {
      return format(selected.from, "MMM d");
    }
    return label;
  }, [label, selected]);

  return (
    <Popover modal open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "gap-1.5",
            isActive || open ? filterChipActiveClass : filterChipInactiveClass,
            className
          )}>
          <span className="whitespace-nowrap">{rangeLabel}</span>
          {isActive && value?.start ? (
            <span
              role="button"
              tabIndex={0}
              className="inline-flex shrink-0"
              aria-label={`Clear ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onChange(undefined);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(undefined);
                }
              }}>
              <X className="h-3.5 w-3.5 shrink-0 opacity-80" strokeWidth={2.5} />
            </span>
          ) : (
            <ChevronDown
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                open && "rotate-180"
              )}
            />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        collisionPadding={16}
        className="z-60 !w-fit max-w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-visible rounded-2xl border border-gray-blue-200 bg-white p-6 shadow-xl"
        onOpenAutoFocus={(e) => e.preventDefault()}>
        <MarketplaceDatePickerPanel
          label={label}
          value={value}
          onChange={onChange}
          onApply={() => onOpenChange(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
